/*
  CODEX CUSTOMIZATION START
  File: custom-product.js
  Scope: product templates
  Notes:
  - Keeps product-page JavaScript separate from Impact core files.
  - Adds a compare-at price to the custom price card when Google Automated Discounts lowers the visible product price client-side.
  CODEX CUSTOMIZATION END
*/

(() => {
  const PRODUCT_INFO_SELECTOR = '.shopify-section--main-product .product-info';
  const CUSTOM_PRICE_BLOCK_SELECTOR = '.product-info__block-item[data-block-type="custom-price"]';
  const GOOGLE_DISCOUNT_BLOCK_SELECTOR = '.product-info__block-item[data-block-id*="google_youtube_automated_discounts_price"]';
  const PRICE_MAIN_COMPARE_CLASS = 'product-info__custom-price-main--has-compare-at';
  const CODEX_ACTIVE_ATTRIBUTE = 'data-codex-google-automated-discount-active';
  const CODEX_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-compare';
  const CODEX_HIDDEN_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-hidden-compare';
  const CODEX_BADGE_ATTRIBUTE = 'data-codex-google-automated-discount-badge';
  const CODEX_BADGE_LIST_ATTRIBUTE = 'data-codex-google-automated-discount-badge-list';
  const CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-main-class';
  const CODEX_LIQUID_COMPARE_ATTRIBUTE = 'data-codex-google-automated-discount-liquid-compare';
  const CODEX_ORIGINAL_PRICE_CENTS_ATTRIBUTE = 'data-codex-google-automated-discount-original-price-cents';
  const CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE = 'data-codex-google-automated-discount-original-price-text';
  const CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE = 'data-codex-google-automated-discount-hidden-price-list-item';
  const CODEX_COMPARE_TEXT_ATTRIBUTE = 'data-codex-google-automated-discount-compare-text';
  const CODEX_COMPARE_SELECTOR = `[${CODEX_COMPARE_ATTRIBUTE}]`;
  const CODEX_BADGE_SELECTOR = `on-sale-badge[${CODEX_BADGE_ATTRIBUTE}]`;
  const SYNC_DELAYS = [0, 250, 750, 1500, 3000, 5000, 8000];
  const COMPARE_ENFORCEMENT_DELAYS = [0, 50, 150, 300, 750, 1500, 3000, 5000];
  const PDP_WISHLIST_BUTTON_SELECTOR = '.product-gallery__wishlist-button';
  const PDP_WISHLIST_CONTROL_SELECTOR = '.product-gallery__wishlist-button-control';
  const PDP_PRODUCT_FORM_SELECTOR = '.shopify-section--main-product form[is="product-form"]';
  const PDP_WISHLIST_OWNED_FORM_ATTRIBUTE = 'data-codex-wishlist-aria-owned';
  const PDP_WISHLIST_SYNC_DELAYS = [0, 100, 250, 500, 1000, 2000];
  const YOTPO_SUMMARY_BLOCK_SELECTOR = '.product-info__block-item[data-block-id*="yotpo_product_reviews_star_rating"]';
  const YOTPO_REVIEWS_FOCUS_TARGET_SELECTOR = '#yotpo-reviews-main-widget';
  const YOTPO_REVIEWS_APP_SELECTOR = '.shopify-app-block[id*="yotpo_product_reviews_reviews"], [id*="yotpo_product_reviews_reviews"]';
  const YOTPO_SYNC_DELAYS = [0, 100, 250, 500, 1000, 2000, 4000, 8000];
  const YOTPO_RATING_FILTER_PATTERN = /^([1-5])\s+stars?\s+by\s+([\d,]+)\s+reviews?$/i;
  const YOTPO_SUMMARY_CLOSE_PATTERN = /close.*summary.*modal/i;
  const YOTPO_READ_ALL_PATTERN = /^read all reviews$/i;
  const YOTPO_SUMMARY_DIALOG_SELECTOR = '.yotpo-reviews-summary-modal-container[role="dialog"]';
  const YOTPO_READ_ALL_FOCUS_WINDOW = 2000;
  const YOTPO_READ_ALL_FOCUS_RETRY_DELAY = 100;

  let syncQueued = false;
  let syncInProgress = false;
  let syncAfterCurrentRun = false;
  let observer = null;
  let observedProductInfo = null;
  let productJsonPath = '';
  let productJsonPromise = null;
  let compareEnforcementScheduled = false;
  let wishlistObserver = null;
  let observedWishlistControl = null;
  let yotpoObserver = null;
  let yotpoSyncQueued = false;
  let yotpoReadAllCleanup = null;
  let yotpoModalObserver = null;
  let observedYotpoDialog = null;

  function parseMoney(value) {
    if (typeof value !== 'string') {
      return NaN;
    }

    const match = value.replace(/\s+/g, ' ').match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return NaN;
    }

    return Number.parseFloat(match[0].replace(/,/g, ''));
  }

  function parseCartAttributes(scriptText) {
    const cartAttributesMatch = scriptText.match(/automatedDiscountsLiquidData\.cartAttributes\s*=\s*({[^\n]*})/);

    if (!cartAttributesMatch) {
      return {};
    }

    try {
      return JSON.parse(cartAttributesMatch[1]);
    } catch (error) {
      return {};
    }
  }

  function getInlineGoogleDiscountData(appBlock) {
    const inlineScript = appBlock ? appBlock.querySelector('script:not([src])') : null;
    const scriptText = inlineScript ? inlineScript.textContent || '' : '';
    const currentPriceMatch = scriptText.match(/automatedDiscountsLiquidData\.currentPrice\s*=\s*([0-9.]+)(?:\s*\/\s*([0-9.]+))?/);
    const formattedCurrentPriceMatch = scriptText.match(/automatedDiscountsLiquidData\.formattedCurrentPrice\s*=\s*(["'])(.*?)\1/);
    const variantIdMatch = scriptText.match(/automatedDiscountsLiquidData\.variantId\s*=\s*(["'])(.*?)\1/);
    let currentPrice = NaN;

    if (currentPriceMatch) {
      currentPrice = Number.parseFloat(currentPriceMatch[1]);

      if (currentPriceMatch[2]) {
        currentPrice /= Number.parseFloat(currentPriceMatch[2]);
      }
    }

    return {
      currentPrice,
      formattedCurrentPrice: formattedCurrentPriceMatch ? formattedCurrentPriceMatch[2] : '',
      variantId: variantIdMatch ? variantIdMatch[2] : '',
      cartAttributes: parseCartAttributes(scriptText),
    };
  }

  function getGoogleDiscountData(appBlock) {
    const inlineData = getInlineGoogleDiscountData(appBlock);

    if (inlineData.variantId || inlineData.formattedCurrentPrice || Number.isFinite(inlineData.currentPrice) || Object.keys(inlineData.cartAttributes).length > 0) {
      return inlineData;
    }

    const globalData = window.automatedDiscountsLiquidData;

    if (globalData && typeof globalData === 'object') {
      return {
        currentPrice: Number(globalData.currentPrice),
        formattedCurrentPrice: globalData.formattedCurrentPrice || '',
        variantId: String(globalData.variantId || ''),
        cartAttributes: globalData.cartAttributes || {},
      };
    }

    return inlineData;
  }

  function decodeGoogleOffer(cartAttributes, variantId) {
    const encodedOffer = cartAttributes && typeof cartAttributes._gad === 'string' ? cartAttributes._gad : '';

    if (encodedOffer === '' || typeof window.atob !== 'function') {
      return {
        price: NaN,
      };
    }

    try {
      const decodedOffer = JSON.parse(window.atob(encodedOffer));
      const offerRecord = decodedOffer && decodedOffer.R ? decodedOffer.R : null;
      const offerId = offerRecord ? String(offerRecord.offerId || '') : '';
      const offerVariantId = offerId.split('_').pop();

      if (variantId && offerVariantId && offerVariantId !== String(variantId)) {
        return {
          price: NaN,
        };
      }

      return {
        price: Number(offerRecord ? offerRecord.price : NaN),
      };
    } catch (error) {
      return {
        price: NaN,
      };
    }
  }

  function decodeBase64Url(value) {
    const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalizedValue.length % 4)) % 4);

    return window.atob(`${normalizedValue}${padding}`);
  }

  function getPv2Token() {
    const currentParams = new URLSearchParams(window.location.search);

    if (currentParams.has('pv2')) {
      return currentParams.get('pv2') || '';
    }

    const shopifyPageUrl = window.__st && typeof window.__st.pageurl === 'string' ? window.__st.pageurl : '';
    const queryStart = shopifyPageUrl.indexOf('?');

    if (queryStart === -1) {
      return '';
    }

    return new URLSearchParams(shopifyPageUrl.slice(queryStart)).get('pv2') || '';
  }

  function hasGoogleOfferSignal() {
    if (getPv2Token() !== '') {
      return true;
    }

    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);
    const appBlock = productInfo ? productInfo.querySelector(GOOGLE_DISCOUNT_BLOCK_SELECTOR) : null;

    if (!appBlock) {
      return false;
    }

    const inlineScript = appBlock.querySelector('script:not([src])');
    const scriptText = inlineScript ? inlineScript.textContent || '' : '';
    const cartAttributes = parseCartAttributes(scriptText);

    if (typeof cartAttributes._gad === 'string' && cartAttributes._gad !== '') {
      return true;
    }

    const globalData = window.automatedDiscountsLiquidData;
    const globalCartAttributes = globalData && typeof globalData === 'object' ? globalData.cartAttributes : null;

    return Boolean(globalCartAttributes && typeof globalCartAttributes._gad === 'string' && globalCartAttributes._gad !== '');
  }

  function decodePv2Offer(variantId) {
    const token = getPv2Token();
    const tokenParts = token.split('.');

    if (tokenParts.length < 2 || typeof window.atob !== 'function') {
      return {
        price: NaN,
      };
    }

    try {
      const offerRecord = JSON.parse(decodeBase64Url(tokenParts[1]));
      const offerId = offerRecord && offerRecord.o ? String(offerRecord.o) : '';
      const offerVariantId = offerId.split('_').pop();

      if (variantId && offerVariantId && offerVariantId !== String(variantId)) {
        return {
          price: NaN,
        };
      }

      if (Number.isFinite(Number(offerRecord.exp)) && Date.now() / 1000 > Number(offerRecord.exp)) {
        return {
          price: NaN,
        };
      }

      return {
        price: Number(offerRecord ? offerRecord.p : NaN),
      };
    } catch (error) {
      return {
        price: NaN,
      };
    }
  }

  function getGoogleOffer(discountData, variantId) {
    const gadOffer = decodeGoogleOffer(discountData.cartAttributes, variantId);

    if (Number.isFinite(gadOffer.price)) {
      return gadOffer;
    }

    return decodePv2Offer(variantId);
  }

  function getProductJsonPath() {
    return `${window.location.pathname.replace(/\/$/, '')}.js`;
  }

  async function getProductJson() {
    const nextProductJsonPath = getProductJsonPath();

    if (productJsonPromise && productJsonPath === nextProductJsonPath) {
      return productJsonPromise;
    }

    productJsonPath = nextProductJsonPath;
    productJsonPromise = window.fetch(nextProductJsonPath, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Unable to load product JSON: ${response.status}`);
      }

      return response.json();
    }).catch(() => null);

    return productJsonPromise;
  }

  function getSelectedVariantId(discountData) {
    if (discountData.variantId) {
      return discountData.variantId;
    }

    const selectedVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');

    return selectedVariantInput ? selectedVariantInput.value : '';
  }

  function getVariantPricing(productJson, variantId) {
    if (!productJson || !Array.isArray(productJson.variants) || variantId === '') {
      return {
        price: NaN,
        compareAtPrice: NaN,
      };
    }

    const variant = productJson.variants.find((candidate) => String(candidate.id) === String(variantId));

    if (!variant) {
      return {
        price: NaN,
        compareAtPrice: NaN,
      };
    }

    return {
      price: Number(variant.price) / 100,
      compareAtPrice: Number(variant.compare_at_price) / 100,
    };
  }

  function getDecimalPlaces(value) {
    const decimalPart = String(value).split('.')[1];

    return decimalPart ? decimalPart.length : 0;
  }

  function formatMoneyFromSample(amount, sampleText) {
    const fallback = `$${amount.toFixed(2)}`;

    if (typeof sampleText !== 'string' || sampleText.trim() === '') {
      return fallback;
    }

    const match = sampleText.match(/-?\d[\d,]*(?:\.\d+)?/);

    if (!match) {
      return fallback;
    }

    const sampleNumber = match[0];
    const decimalPlaces = getDecimalPlaces(sampleNumber);
    const hasThousandsSeparator = sampleNumber.includes(',');
    let formattedAmount = amount.toFixed(decimalPlaces);

    if (hasThousandsSeparator) {
      const amountParts = formattedAmount.split('.');
      amountParts[0] = amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedAmount = amountParts.join('.');
    }

    return `${sampleText.slice(0, match.index)}${formattedAmount}${sampleText.slice(match.index + sampleNumber.length)}`;
  }

  function getCompareAtPriceTextFromSavings(displayPriceText, savingsText, moneyFormatSample) {
    const displayPrice = parseMoney(displayPriceText);
    const savingsAmount = parseMoney(savingsText);

    if (!Number.isFinite(displayPrice) || !Number.isFinite(savingsAmount)) {
      return '';
    }

    return formatMoneyFromSample(displayPrice + savingsAmount, moneyFormatSample);
  }

  function createCompareAtPrice(compareAtPriceText) {
    const compareAtPrice = document.createElement('span');

    compareAtPrice.className = 'product-info__custom-price-google-compare text-subdued';
    compareAtPrice.setAttribute(CODEX_COMPARE_ATTRIBUTE, 'true');
    setCompareAtPriceText(compareAtPrice, compareAtPriceText);

    return compareAtPrice;
  }

  function getDisplayedCompareAtPrice(priceRow) {
    return priceRow.querySelector(`${CODEX_COMPARE_SELECTOR}:not([${CODEX_LIQUID_COMPARE_ATTRIBUTE}])`);
  }

  function setCompareAtPriceText(compareAtPrice, compareAtPriceText) {
    let screenReaderLabel = compareAtPrice.querySelector('.sr-only');
    const priceList = compareAtPrice.closest('.product-info__custom-price-price-row')?.querySelector('.price-list');
    const regularPriceLabel = priceList?.dataset.codexRegularPriceLabel || 'Regular price';

    compareAtPrice.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);
    compareAtPrice.setAttribute('aria-label', `${regularPriceLabel} ${compareAtPriceText}`);

    if (
      screenReaderLabel instanceof HTMLElement &&
      screenReaderLabel.parentElement === compareAtPrice &&
      screenReaderLabel.textContent === regularPriceLabel &&
      compareAtPrice.childNodes.length === 2 &&
      compareAtPrice.childNodes[1].nodeType === Node.TEXT_NODE &&
      compareAtPrice.childNodes[1].textContent === compareAtPriceText
    ) {
      return;
    }

    screenReaderLabel = document.createElement('span');
    screenReaderLabel.className = 'sr-only';
    screenReaderLabel.textContent = regularPriceLabel;

    compareAtPrice.replaceChildren(screenReaderLabel, document.createTextNode(compareAtPriceText));
  }

  function setSalePriceSemanticLabel(salePrice, label) {
    let screenReaderLabel = Array.from(salePrice.children).find((child) => child.classList.contains('sr-only'));

    if (!screenReaderLabel) {
      screenReaderLabel = document.createElement('span');
      screenReaderLabel.className = 'sr-only';
      salePrice.prepend(screenReaderLabel);
    }

    if (screenReaderLabel.textContent !== label) {
      screenReaderLabel.textContent = label;
    }
  }

  function syncSalePrice(salePrice, salePriceText) {
    const priceList = salePrice.closest('.price-list');
    const salePriceLabel = priceList?.dataset.codexSalePriceLabel || 'Sale price';
    const currentSalePrice = parseMoney(salePrice.textContent || '');
    const nextSalePrice = parseMoney(salePriceText);

    setSalePriceSemanticLabel(salePrice, salePriceLabel);

    if (Number.isFinite(currentSalePrice) && Number.isFinite(nextSalePrice) && Math.abs(currentSalePrice - nextSalePrice) < 0.005) {
      return;
    }

    const screenReaderLabel = document.createElement('span');

    screenReaderLabel.className = 'sr-only';
    screenReaderLabel.textContent = salePriceLabel;

    salePrice.replaceChildren(screenReaderLabel, document.createTextNode(salePriceText));
  }

  function createDiscountBadge(savingsText) {
    const badge = document.createElement('on-sale-badge');

    badge.className = 'badge badge--on-sale';
    badge.setAttribute(CODEX_BADGE_ATTRIBUTE, 'true');
    badge.textContent = `Save ${savingsText}`;

    return badge;
  }

  function getLiquidCompareAtPrice(priceRow) {
    return priceRow.querySelector(`[${CODEX_LIQUID_COMPARE_ATTRIBUTE}]`);
  }

  function getLiquidOriginalPrice(priceRow) {
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);

    if (!liquidCompareAtPrice) {
      return NaN;
    }

    const cents = Number.parseFloat(liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_CENTS_ATTRIBUTE) || '');

    if (Number.isFinite(cents)) {
      return cents / 100;
    }

    return parseMoney(liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE) || '');
  }

  function getLiquidOriginalPriceText(priceRow) {
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);

    return liquidCompareAtPrice ? liquidCompareAtPrice.getAttribute(CODEX_ORIGINAL_PRICE_TEXT_ATTRIBUTE) || '' : '';
  }

  function isLiquidCompareAtPrice(compareAtPrice) {
    return compareAtPrice.hasAttribute(CODEX_LIQUID_COMPARE_ATTRIBUTE);
  }

  function normalizeLiquidCompareAtPriceSource(liquidCompareAtPrice) {
    if (!liquidCompareAtPrice) {
      return;
    }

    liquidCompareAtPrice.removeAttribute(CODEX_COMPARE_ATTRIBUTE);
    liquidCompareAtPrice.className = 'product-info__custom-price-google-source';
    liquidCompareAtPrice.hidden = true;
    liquidCompareAtPrice.setAttribute('aria-hidden', 'true');
    liquidCompareAtPrice.style.removeProperty('display');
    liquidCompareAtPrice.style.removeProperty('text-decoration-line');
  }

  function setGoogleDiscountActive(priceRow, isActive) {
    const priceMain = priceRow.closest('.product-info__custom-price-main');

    if (isActive) {
      if (!priceRow.hasAttribute(CODEX_ACTIVE_ATTRIBUTE)) {
        priceRow.setAttribute(CODEX_ACTIVE_ATTRIBUTE, 'true');
      }

      if (priceMain && !priceMain.classList.contains(PRICE_MAIN_COMPARE_CLASS)) {
        priceMain.classList.add(PRICE_MAIN_COMPARE_CLASS);
        priceMain.setAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE, 'true');
      }

      return;
    }

    priceRow.removeAttribute(CODEX_ACTIVE_ATTRIBUTE);

    if (priceMain && priceMain.hasAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE)) {
      priceMain.classList.remove(PRICE_MAIN_COMPARE_CLASS);
      priceMain.removeAttribute(CODEX_PRICE_MAIN_COMPARE_ATTRIBUTE);
    }
  }

  function removeInjectedCompareAtPrice(priceRow, preserveLiquidCompareAtPrice = true) {
    normalizeLiquidCompareAtPriceSource(getLiquidCompareAtPrice(priceRow));
    priceRow.removeAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE);

    priceRow.querySelectorAll(CODEX_COMPARE_SELECTOR).forEach((injectedCompareAtPrice) => {
      if (!preserveLiquidCompareAtPrice || !isLiquidCompareAtPrice(injectedCompareAtPrice)) {
        injectedCompareAtPrice.remove();
        return;
      }

      injectedCompareAtPrice.hidden = false;
      injectedCompareAtPrice.style.removeProperty('display');
      injectedCompareAtPrice.style.removeProperty('text-decoration-line');
    });

    restoreHiddenPriceListItems(priceRow);
    restoreHiddenCompareAtPrices(priceRow);
    setGoogleDiscountActive(priceRow, false);
  }

  function removeNonNativeCompareAtPrices(priceRow) {
    removeInjectedCompareAtPrice(priceRow);

    priceRow.querySelectorAll('compare-at-price').forEach((compareAtPrice) => {
      compareAtPrice.remove();
    });
  }

  function hideGoogleCompareAtPrices(priceRow) {
    priceRow.querySelectorAll('compare-at-price').forEach((compareAtPrice) => {
      if (!compareAtPrice.hasAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE)) {
        compareAtPrice.setAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE, 'true');
      }

      if (!compareAtPrice.hidden) {
        compareAtPrice.hidden = true;
      }

      if (compareAtPrice.style.display !== 'none') {
        compareAtPrice.style.display = 'none';
      }
    });
  }

  function hideExtraPriceListItems(priceList, salePrice) {
    Array.from(priceList.children).forEach((priceItem) => {
      if (priceItem === salePrice) {
        return;
      }

      priceItem.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
      priceItem.hidden = true;
      priceItem.style.display = 'none';
    });
  }

  function restoreHiddenPriceListItems(priceRow) {
    priceRow.querySelectorAll(`[${CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE}]`).forEach((priceItem) => {
      priceItem.removeAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE);
      priceItem.hidden = false;
      priceItem.style.removeProperty('display');
    });
  }

  function restoreHiddenCompareAtPrices(priceRow) {
    priceRow.querySelectorAll(`compare-at-price[${CODEX_HIDDEN_COMPARE_ATTRIBUTE}]`).forEach((compareAtPrice) => {
      compareAtPrice.removeAttribute(CODEX_HIDDEN_COMPARE_ATTRIBUTE);
      compareAtPrice.hidden = false;
      compareAtPrice.style.removeProperty('display');
    });
  }

  function hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, displayPriceText) {
    const displayPrice = parseMoney(displayPriceText);

    priceRow.querySelectorAll('compare-at-price, s, del, [style*="line-through"], .text-subdued').forEach((candidate) => {
      if (
        candidate === compareAtPrice ||
        compareAtPrice.contains(candidate) ||
        candidate === salePrice ||
        salePrice.contains(candidate) ||
        candidate.closest(CODEX_BADGE_SELECTOR)
      ) {
        return;
      }

      if (candidate.hasAttribute(CODEX_LIQUID_COMPARE_ATTRIBUTE)) {
        normalizeLiquidCompareAtPriceSource(candidate);
        return;
      }

      if (priceList.contains(candidate)) {
        candidate.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
        candidate.hidden = true;
        candidate.style.display = 'none';
        return;
      }

      const candidatePrice = parseMoney(candidate.textContent || '');

      if (!Number.isFinite(displayPrice) || !Number.isFinite(candidatePrice) || Math.abs(candidatePrice - displayPrice) < 0.005) {
        candidate.setAttribute(CODEX_HIDDEN_PRICE_LIST_ITEM_ATTRIBUTE, 'true');
        candidate.hidden = true;
        candidate.style.display = 'none';
      }
    });
  }

  function forceActiveCompareAtPrices() {
    document.querySelectorAll(`.product-info__custom-price-price-row[${CODEX_ACTIVE_ATTRIBUTE}][${CODEX_COMPARE_TEXT_ATTRIBUTE}]`).forEach((priceRow) => {
      const compareAtPriceText = priceRow.getAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE) || '';
      const priceList = priceRow.querySelector('.price-list');
      const salePrice = priceList ? priceList.querySelector('sale-price') : null;
      const compareAtPrice = getDisplayedCompareAtPrice(priceRow);

      if (!priceList || !salePrice || !compareAtPrice || compareAtPriceText === '') {
        return;
      }

      compareAtPrice.hidden = false;
      compareAtPrice.style.display = 'inline';
      compareAtPrice.style.removeProperty('text-decoration-line');
      setCompareAtPriceText(compareAtPrice, compareAtPriceText);

      if (compareAtPrice.parentElement !== priceRow || compareAtPrice.previousElementSibling !== priceList) {
        priceList.insertAdjacentElement('afterend', compareAtPrice);
      }

      hideGoogleCompareAtPrices(priceRow);
      hideExtraPriceListItems(priceList, salePrice);
      hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, salePrice.textContent || '');
    });
  }

  function scheduleCompareAtEnforcement() {
    if (compareEnforcementScheduled) {
      return;
    }

    compareEnforcementScheduled = true;

    COMPARE_ENFORCEMENT_DELAYS.forEach((delay, index) => {
      window.setTimeout(() => {
        forceActiveCompareAtPrices();

        if (index === COMPARE_ENFORCEMENT_DELAYS.length - 1) {
          compareEnforcementScheduled = false;
        }
      }, delay);
    });
  }

  function syncCompareAtPrice(priceRow, priceList, salePrice, compareAtPriceText, displayPriceText) {
    setGoogleDiscountActive(priceRow, true);
    priceRow.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);
    normalizeLiquidCompareAtPriceSource(getLiquidCompareAtPrice(priceRow));
    hideGoogleCompareAtPrices(priceRow);
    hideExtraPriceListItems(priceList, salePrice);

    let compareAtPrice = getDisplayedCompareAtPrice(priceRow);

    if (!compareAtPrice) {
      compareAtPrice = createCompareAtPrice(compareAtPriceText);
      priceList.insertAdjacentElement('afterend', compareAtPrice);
    }

    priceRow.querySelectorAll(CODEX_COMPARE_SELECTOR).forEach((candidate) => {
      if (candidate !== compareAtPrice && !isLiquidCompareAtPrice(candidate)) {
        candidate.remove();
      }
    });

    if (!compareAtPrice.hasAttribute(CODEX_COMPARE_ATTRIBUTE)) {
      compareAtPrice.setAttribute(CODEX_COMPARE_ATTRIBUTE, 'true');
    }

    compareAtPrice.setAttribute(CODEX_COMPARE_TEXT_ATTRIBUTE, compareAtPriceText);

    if (compareAtPrice.className !== 'product-info__custom-price-google-compare text-subdued') {
      compareAtPrice.className = 'product-info__custom-price-google-compare text-subdued';
    }
    compareAtPrice.hidden = false;
    compareAtPrice.style.display = 'inline';
    compareAtPrice.style.removeProperty('text-decoration-line');
    setCompareAtPriceText(compareAtPrice, compareAtPriceText);

    if (compareAtPrice.parentElement !== priceRow || compareAtPrice.previousElementSibling !== priceList) {
      priceList.insertAdjacentElement('afterend', compareAtPrice);
    }

    priceRow.querySelectorAll('compare-at-price').forEach((candidate) => {
      setCompareAtPriceText(candidate, compareAtPriceText);
    });

    hideConflictingCompareAtPrices(priceRow, priceList, salePrice, compareAtPrice, displayPriceText);
    scheduleCompareAtEnforcement();
  }

  function removeInjectedDiscountBadge(priceRow) {
    const injectedBadge = priceRow.querySelector(CODEX_BADGE_SELECTOR);

    if (!injectedBadge) {
      return;
    }

    const badgeList = injectedBadge.closest('.product-info__badge-list');

    injectedBadge.remove();

    if (badgeList && badgeList.hasAttribute(CODEX_BADGE_LIST_ATTRIBUTE) && badgeList.children.length === 0) {
      badgeList.remove();
    }
  }

  function syncDiscountBadge(priceRow, priceList, savingsText) {
    let badgeList = priceRow.querySelector('.product-info__badge-list');
    let badge = priceRow.querySelector(CODEX_BADGE_SELECTOR);

    if (!badgeList) {
      badgeList = document.createElement('div');
      badgeList.className = 'product-info__badge-list';
      badgeList.setAttribute(CODEX_BADGE_LIST_ATTRIBUTE, 'true');
      (priceRow.querySelector(CODEX_COMPARE_SELECTOR) || priceList).insertAdjacentElement('afterend', badgeList);
    }

    if (!badge) {
      badge = createDiscountBadge(savingsText);
      badgeList.append(badge);
      return;
    }

    const nextBadgeText = `Save ${savingsText}`;

    if (badge.textContent.trim() !== nextBadgeText) {
      badge.textContent = nextBadgeText;
    }
  }

  async function syncGoogleAutomatedDiscountCompareAt() {
    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);
    const priceBlock = productInfo ? productInfo.querySelector(CUSTOM_PRICE_BLOCK_SELECTOR) : null;
    const appBlock = productInfo ? productInfo.querySelector(GOOGLE_DISCOUNT_BLOCK_SELECTOR) : null;

    if (!priceBlock || !appBlock) {
      return false;
    }

    const priceList = priceBlock.querySelector('.product-info__custom-price-price-row .price-list');
    const priceRow = priceList ? priceList.closest('.product-info__custom-price-price-row') : null;
    const salePrice = priceList ? priceList.querySelector('sale-price') : null;

    if (!priceList || !priceRow || !salePrice) {
      return false;
    }

    const discountData = getGoogleDiscountData(appBlock);
    const variantId = getSelectedVariantId(discountData);
    const googleOffer = getGoogleOffer(discountData, variantId);
    const liquidCompareAtPrice = getLiquidCompareAtPrice(priceRow);
    const liquidOriginalPrice = getLiquidOriginalPrice(priceRow);
    const liquidOriginalPriceText = getLiquidOriginalPriceText(priceRow);
    const visiblePrice = parseMoney(salePrice.textContent || '');
    let variantPricing = {
      price: NaN,
      compareAtPrice: NaN,
    };
    let originalPrice = liquidOriginalPrice;
    let hasNativeShopifyCompareAt = !liquidCompareAtPrice && Boolean(priceList.querySelector('compare-at-price'));

    if (!Number.isFinite(originalPrice) && !hasNativeShopifyCompareAt) {
      const productJson = await getProductJson();

      variantPricing = getVariantPricing(productJson, variantId);
      originalPrice = variantPricing.price;
      hasNativeShopifyCompareAt = Number.isFinite(variantPricing.compareAtPrice) && variantPricing.compareAtPrice > originalPrice;
    }

    const moneyFormatSample = liquidOriginalPriceText || discountData.formattedCurrentPrice || '$0.00';
    const originalPriceText = Number.isFinite(originalPrice)
      ? (Number.isFinite(liquidOriginalPrice) && Math.abs(originalPrice - liquidOriginalPrice) < 0.005 && liquidOriginalPriceText !== '' ? liquidOriginalPriceText : formatMoneyFromSample(originalPrice, moneyFormatSample))
      : liquidOriginalPriceText;
    const googleOfferPrice = googleOffer.price;
    const displayPrice = Number.isFinite(googleOfferPrice) && Number.isFinite(originalPrice) && googleOfferPrice < originalPrice - 0.005 ? googleOfferPrice : visiblePrice;
    const displayPriceText = Number.isFinite(displayPrice) ? formatMoneyFromSample(displayPrice, moneyFormatSample) : '';
    const savingsAmount = Number.isFinite(originalPrice) && Number.isFinite(displayPrice) ? originalPrice - displayPrice : NaN;
    const savingsText = Number.isFinite(savingsAmount) ? formatMoneyFromSample(savingsAmount, moneyFormatSample) : '';
    const compareAtPriceText = getCompareAtPriceTextFromSavings(displayPriceText, savingsText, moneyFormatSample) || originalPriceText;
    const hasGoogleDiscount = Number.isFinite(originalPrice) && Number.isFinite(displayPrice) && displayPrice < originalPrice - 0.005;

    if (hasNativeShopifyCompareAt) {
      setSalePriceSemanticLabel(salePrice, priceList.dataset.codexSalePriceLabel || 'Sale price');
      removeInjectedCompareAtPrice(priceRow, false);
      removeInjectedDiscountBadge(priceRow);
      return false;
    }

    if (!hasGoogleDiscount || originalPriceText === '' || savingsText === '') {
      setSalePriceSemanticLabel(salePrice, priceList.dataset.codexPriceLabel || 'Price');
      removeNonNativeCompareAtPrices(priceRow);
      removeInjectedDiscountBadge(priceRow);
      return false;
    }

    if (displayPriceText !== '') {
      syncSalePrice(salePrice, displayPriceText);
    }

    syncCompareAtPrice(priceRow, priceList, salePrice, compareAtPriceText, displayPriceText);
    syncDiscountBadge(priceRow, priceList, savingsText);

    return true;
  }

  function queueSync() {
    if (syncQueued) {
      return;
    }

    syncQueued = true;

    window.requestAnimationFrame(async () => {
      syncQueued = false;

      if (syncInProgress) {
        syncAfterCurrentRun = true;
        return;
      }

      syncInProgress = true;

      try {
        const hasActiveGoogleDiscount = await syncGoogleAutomatedDiscountCompareAt();

        if (hasActiveGoogleDiscount) {
          observeProductInfo();
        }
      } finally {
        syncInProgress = false;

        if (syncAfterCurrentRun) {
          syncAfterCurrentRun = false;
          queueSync();
        }
      }
    });
  }

  function observeProductInfo() {
    const productInfo = document.querySelector(PRODUCT_INFO_SELECTOR);

    if (!(productInfo instanceof HTMLElement) || observedProductInfo === productInfo) {
      return;
    }

    if (observer) {
      observer.disconnect();
    }

    observedProductInfo = productInfo;
    observer = new MutationObserver(queueSync);
    observer.observe(productInfo, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function getProductGalleryWishlistButton() {
    const button = document.querySelector(PDP_WISHLIST_BUTTON_SELECTOR);

    return button instanceof HTMLElement ? button : null;
  }

  function syncProductWishlistReadingOrder() {
    const wishlistButton = getProductGalleryWishlistButton();
    const productForm = Array.from(document.querySelectorAll(PDP_PRODUCT_FORM_SELECTOR)).find((form) => (
      form instanceof HTMLFormElement && form.querySelector('button[type="submit"]')
    ));

    if (!(productForm instanceof HTMLFormElement)) {
      return;
    }

    const previouslyOwnedId = productForm.getAttribute(PDP_WISHLIST_OWNED_FORM_ATTRIBUTE);
    const ownedIds = (productForm.getAttribute('aria-owns') || '')
      .split(/\s+/)
      .filter((id) => id && id !== previouslyOwnedId);

    if (!(wishlistButton instanceof HTMLElement)) {
      if (previouslyOwnedId) {
        if (ownedIds.length > 0) {
          productForm.setAttribute('aria-owns', ownedIds.join(' '));
        } else {
          productForm.removeAttribute('aria-owns');
        }
        productForm.removeAttribute(PDP_WISHLIST_OWNED_FORM_ATTRIBUTE);
      }
      return;
    }

    if (!wishlistButton.id) {
      wishlistButton.id = 'product-wishlist-pdp';
    }

    if (!ownedIds.includes(wishlistButton.id)) {
      ownedIds.push(wishlistButton.id);
    }

    productForm.setAttribute('aria-owns', ownedIds.join(' '));
    productForm.setAttribute(PDP_WISHLIST_OWNED_FORM_ATTRIBUTE, wishlistButton.id);
  }

  function getProductGalleryWishlistControl(button = getProductGalleryWishlistButton()) {
    if (!button) {
      return null;
    }

    return button.querySelector(PDP_WISHLIST_CONTROL_SELECTOR)
      || button.querySelector('button')
      || button.shadowRoot?.querySelector('button')
      || button.wishlistButton
      || button.button
      || null;
  }

  function syncProductGalleryWishlistState() {
    const button = getProductGalleryWishlistButton();
    syncProductWishlistReadingOrder();
    const control = getProductGalleryWishlistControl(button);

    if (!button || !(control instanceof HTMLElement)) {
      return;
    }

    const checkedState = control.getAttribute('aria-checked');
    const savedState = checkedState === 'true' || checkedState === 'false'
      ? checkedState
      : control.getAttribute('aria-pressed');

    if (savedState !== 'true' && savedState !== 'false') {
      return;
    }

    // Match Wishlist Power's checked-state contract without exposing an
    // unsupported aria-checked state on a button-role control.
    if (control.hasAttribute('aria-pressed')) {
      control.removeAttribute('aria-pressed');
    }
    if (control.getAttribute('aria-checked') !== savedState) {
      control.setAttribute('aria-checked', savedState);
    }

    if (control.getAttribute('role') !== 'checkbox') {
      control.setAttribute('role', 'checkbox');
    }

    const name = window.themeVariables?.strings?.wishlistControlLabel || 'Wishlist';
    let label = control.querySelector('[data-codex-wishlist-label]');
    if (!label) {
      label = document.createElement('span');
      label.id = 'codex-wishlist-pdp-label';
      label.hidden = true;
      label.setAttribute('data-codex-wishlist-label', '');
      control.appendChild(label);
    }
    if (label.textContent !== name) label.textContent = name;
    if (control.getAttribute('aria-labelledby') !== label.id) {
      control.setAttribute('aria-labelledby', label.id);
    }
    // aria-labelledby supplies the stable name. Do not fight the app's
    // Add/Remove fallback aria-label updates with more attribute mutations.
  }

  function observeProductGalleryWishlistButton() {
    const control = getProductGalleryWishlistControl();

    if (!(control instanceof HTMLElement) || observedWishlistControl === control) {
      return;
    }

    if (wishlistObserver) {
      wishlistObserver.disconnect();
    }

    observedWishlistControl = control;
    syncProductGalleryWishlistState();

    wishlistObserver = new MutationObserver(() => {
      syncProductGalleryWishlistState();
    });

    wishlistObserver.observe(control, {
      attributes: true,
      attributeFilter: ['aria-checked', 'aria-pressed', 'aria-label', 'aria-busy'],
    });
  }

  function scheduleProductGalleryWishlistSync() {
    PDP_WISHLIST_SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(observeProductGalleryWishlistButton, delay);
    });
  }

  function isElementVisible(element) {
    if (!(element instanceof HTMLElement) || element.hidden || element.closest('[hidden], [aria-hidden="true"]')) {
      return false;
    }

    const style = window.getComputedStyle(element);

    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  function getYotpoSummaryControl(block) {
    if (!(block instanceof HTMLElement)) {
      return null;
    }

    return Array.from(block.querySelectorAll('a[href], button, [role="button"], [tabindex]:not([tabindex="-1"])')).find(isElementVisible) || null;
  }

  function normalizeYotpoSummaryName(block) {
    const control = getYotpoSummaryControl(block);

    if (!control) {
      return;
    }

    const currentLabel = (control.getAttribute('aria-label') || '').trim();
    const scoreText = block.querySelector('.yotpo-sr-bottom-line-score')?.textContent || block.textContent || '';
    const reviewText = block.querySelector('.yotpo-sr-bottom-line-text')?.textContent || block.textContent || '';
    const score = scoreText.match(/\b([0-5](?:\.\d+)?)\b/)?.[1]
      || currentLabel.match(/\b([0-5](?:\.\d+)?)\s+out(?:\s+of)?\s+5\b/i)?.[1];
    const count = reviewText.match(/\b([\d,]+)\s+reviews?\b/i)?.[1]
      || currentLabel.match(/\b([\d,]+)\s+reviews?\b/i)?.[1];

    if (score && count) {
      const reviewWord = count.replace(/,/g, '') === '1' ? 'review' : 'reviews';

      control.setAttribute('aria-label', `Rated ${score} out of 5 stars based on ${count} ${reviewWord}. Select to access customer reviews.`);
      control.setAttribute('data-codex-yotpo-summary-control', '');

      // Yotpo exposes the same score/count as readable descendants of its
      // already-named button. Keep them visible, but make the button one atomic
      // accessibility object so browse mode does not announce them a second time.
      Array.from(control.children).forEach((child) => child.setAttribute('aria-hidden', 'true'));
    }
  }

  function normalizeYotpoRatingFilters() {
    document.querySelectorAll(YOTPO_REVIEWS_APP_SELECTOR).forEach((app) => {
      app.querySelectorAll('[aria-label]').forEach((control) => {
        const currentLabel = (control.getAttribute('aria-label') || '').trim();
        const match = currentLabel.match(YOTPO_RATING_FILTER_PATTERN);

        if (!match) {
          return;
        }

        const stars = match[1];
        const reviewCount = match[2];
        const starWord = stars === '1' ? 'star' : 'stars';
        const reviewWord = reviewCount.replace(/,/g, '') === '1' ? 'review' : 'reviews';

        control.setAttribute('aria-label', `${stars} ${starWord}, ${reviewCount} ${reviewWord}, filter reviews`);
        control.setAttribute('data-codex-yotpo-rating-filter', stars);
      });
    });
  }

  function getElementAccessibleText(element) {
    const labelledBy = (element.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean)
      .map((id) => document.getElementById(id)?.textContent || '').join(' ').trim();

    return (labelledBy || element.getAttribute('aria-label') || element.getAttribute('alt') || element.textContent || '').trim().replace(/\s+/g, ' ');
  }

  function setYotpoAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) {
      element.setAttribute(name, value);
    }
  }

  function getYotpoSummaryDialogs() {
    return Array.from(document.querySelectorAll('[role="dialog"], [aria-modal="true"]')).filter((dialog) => {
      if (!isElementVisible(dialog)) {
        return false;
      }

      const closeButton = Array.from(dialog.querySelectorAll('button, [role="button"]')).find((control) => (
        YOTPO_SUMMARY_CLOSE_PATTERN.test(getElementAccessibleText(control))
      ));
      const readAllControl = Array.from(dialog.querySelectorAll('button, a[href], [role="button"]')).find((control) => (
        YOTPO_READ_ALL_PATTERN.test(getElementAccessibleText(control))
      ));
      const identity = `${dialog.id} ${dialog.className}`.toLowerCase();
      const summaryText = getElementAccessibleText(dialog);

      return Boolean(
        closeButton
        || (readAllControl && /customers say|review summary|summary by topics/i.test(summaryText))
        || (/yotpo/.test(identity) && /summary/.test(identity))
      );
    });
  }

  function getYotpoSummaryDialog() {
    return getYotpoSummaryDialogs()[0] || null;
  }

  function normalizeYotpoSummaryTopicHeadings(dialog) {
    dialog.querySelectorAll('.yotpo-summary-title').forEach((heading) => {
      const feedbackImage = Array.from(heading.querySelectorAll('img[alt], [role="img"][aria-label]')).find((image) => (
        /feedback/i.test(getElementAccessibleText(image))
      ));
      const topicElement = heading.querySelector('.yotpo-summary-topic');

      if (!feedbackImage || !topicElement || heading.querySelector('a[href], button, input, [tabindex]')) {
        return;
      }

      const feedbackType = getElementAccessibleText(feedbackImage);
      const topic = getElementAccessibleText(topicElement);

      if (!feedbackType || !topic) {
        return;
      }

      setYotpoAttribute(heading, 'aria-label', `${feedbackType}: ${topic}`);
      Array.from(heading.children).forEach((child) => setYotpoAttribute(child, 'aria-hidden', 'true'));
    });
  }

  function normalizeYotpoSummaryDialog(dialog) {
    if (!(dialog instanceof HTMLElement)) {
      return;
    }

    const title = dialog.querySelector('.yotpo-summary-header-title');
    const dialogName = (title?.textContent || getElementAccessibleText(dialog) || 'Customer review summary')
      .trim()
      .replace(/\s+/g, ' ');

    setYotpoAttribute(dialog, 'aria-label', dialogName);
    dialog.removeAttribute('aria-labelledby');
    setYotpoAttribute(dialog, 'data-codex-yotpo-summary-dialog', '');

    // The dialog name already announces its visible title. Hide exact repeated
    // title nodes so VoiceOver does not announce “Customers say” two more times.
    const repeatedTitles = new Set([
      title,
      ...Array.from(dialog.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]')).filter((heading) => (
        getElementAccessibleText(heading) === dialogName
      )),
    ]);

    repeatedTitles.forEach((title) => {
      if (title instanceof HTMLElement) {
        setYotpoAttribute(title, 'aria-hidden', 'true');
      }
    });

    const closeButton = Array.from(dialog.querySelectorAll('button, [role="button"]')).find((control) => (
      YOTPO_SUMMARY_CLOSE_PATTERN.test(getElementAccessibleText(control))
    ));

    if (closeButton) {
      const closeName = getElementAccessibleText(closeButton);
      // Yotpo names this button through the adjacent tooltip, not aria-label.
      // Preserve that dynamic name before detaching the duplicate tooltip.
      setYotpoAttribute(closeButton, 'aria-label', closeName);
      closeButton.removeAttribute('aria-labelledby');
      Array.from(closeButton.children).forEach((child) => setYotpoAttribute(child, 'aria-hidden', 'true'));

      const describedByIds = (closeButton.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      const retainedDescriptionIds = describedByIds.filter((id) => {
        const description = document.getElementById(id);

        if (description && YOTPO_SUMMARY_CLOSE_PATTERN.test(getElementAccessibleText(description))) {
          setYotpoAttribute(description, 'aria-hidden', 'true');
          return false;
        }

        return true;
      });

      if (retainedDescriptionIds.length > 0) {
        setYotpoAttribute(closeButton, 'aria-describedby', retainedDescriptionIds.join(' '));
      } else {
        closeButton.removeAttribute('aria-describedby');
      }

      dialog.querySelectorAll('[role="tooltip"]').forEach((tooltip) => {
        if (YOTPO_SUMMARY_CLOSE_PATTERN.test(getElementAccessibleText(tooltip))) {
          setYotpoAttribute(tooltip, 'aria-hidden', 'true');
        }
      });
    }

    normalizeYotpoSummaryTopicHeadings(dialog);
  }

  function normalizeYotpoSummaryModal() {
    const dialog = getYotpoSummaryDialog();

    if (observedYotpoDialog !== dialog) {
      yotpoModalObserver?.disconnect();
      observedYotpoDialog = dialog;
      if (dialog) {
        yotpoModalObserver = new MutationObserver(() => normalizeYotpoSummaryDialog(dialog));
        yotpoModalObserver.observe(dialog, {
          subtree: true, childList: true, characterData: true, attributes: true,
          attributeFilter: ['aria-label', 'aria-labelledby', 'aria-describedby', 'aria-hidden'],
        });
      }
    }
    if (dialog) {
      normalizeYotpoSummaryDialog(dialog);
    }
  }

  function syncYotpoAccessibility() {
    document.querySelectorAll(YOTPO_SUMMARY_BLOCK_SELECTOR).forEach(normalizeYotpoSummaryName);
    normalizeYotpoRatingFilters();
    normalizeYotpoSummaryModal();
  }

  function queueYotpoAccessibilitySync() {
    if (yotpoSyncQueued) {
      return;
    }

    yotpoSyncQueued = true;
    window.requestAnimationFrame(() => {
      yotpoSyncQueued = false;
      syncYotpoAccessibility();
    });
  }

  function getYotpoStatusRegion() {
    const app = document.querySelector(YOTPO_REVIEWS_APP_SELECTOR);
    const section = app?.closest('.shopify-section');

    if (!(section instanceof HTMLElement)) {
      return null;
    }

    let status = section.querySelector('[data-codex-yotpo-filter-status]');

    if (!status) {
      status = document.createElement('div');
      status.className = 'sr-only';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('data-codex-yotpo-filter-status', '');
      section.append(status);
    }

    return status;
  }

  function focusYotpoReviewsGroup() {
    const group = document.querySelector(YOTPO_REVIEWS_FOCUS_TARGET_SELECTOR);

    if (!(group instanceof HTMLElement) || !isElementVisible(group) || group.closest('[inert]')) {
      return false;
    }

    // This is Yotpo's native focus target inside the labeled Customer Reviews
    // group. Keep it at a programmatic-only tabindex so it is not inserted
    // into the normal Tab/Shift+Tab sequence.
    if (group.getAttribute('tabindex') !== '-1') {
      group.setAttribute('tabindex', '-1');
    }
    // Match Yotpo's native star-rating handler so both entry points create the
    // same scroll and accessibility context.
    group.scrollIntoView({ behavior: 'smooth' });
    if (document.activeElement !== group) {
      group.focus({ preventScroll: true });
    }
    return document.activeElement === group;
  }

  function handleYotpoClick(event) {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const ratingFilter = target.closest('[data-codex-yotpo-rating-filter]');

    if (!ratingFilter || !ratingFilter.closest(YOTPO_REVIEWS_APP_SELECTOR)) {
      return;
    }

    const stars = ratingFilter.getAttribute('data-codex-yotpo-rating-filter');

    window.setTimeout(() => {
      queueYotpoAccessibilitySync();

      if (!ratingFilter.isConnected && document.activeElement === document.body) {
        document.querySelector(`[data-codex-yotpo-rating-filter="${stars}"]`)?.focus();
      }

      const status = getYotpoStatusRegion();
      if (status) {
        status.textContent = `Reviews filtered to ${stars} ${stars === '1' ? 'star' : 'stars'}.`;
      }
    }, 300);
  }

  function handleYotpoReadAllReviews(event) {
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const target = event.target instanceof Element ? event.target : null;
    const control = eventPath.find((element) => (
      element instanceof Element && element.matches('button, a[href], [role="button"]')
    )) || target?.closest('button, a[href], [role="button"]');
    const dialog = eventPath.find((element) => (
      element instanceof HTMLElement && element.matches('[data-codex-yotpo-summary-dialog], [role="dialog"], [aria-modal="true"]')
    )) || target?.closest('[data-codex-yotpo-summary-dialog], [role="dialog"], [aria-modal="true"]');

    if (!dialog?.matches(YOTPO_SUMMARY_DIALOG_SELECTOR) || !control || !YOTPO_READ_ALL_PATTERN.test(getElementAccessibleText(control))) {
      return;
    }

    yotpoReadAllCleanup?.();
    // The vendor's actual Read-all handler uses closeSummary(false), so its
    // stored return-focus hook is deliberately bypassed. Wait for the real
    // portal removal AND restored background accessibility instead of changing
    // that private vendor state.
    let completed = false;
    let timeout;
    let focusFrame;
    let retryTimer;
    let focusDeadline = 0;
    const cleanup = () => {
      completed = true;
      closeObserver.disconnect();
      window.clearTimeout(timeout);
      window.clearTimeout(retryTimer);
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', cancelForNavigation, true);
      document.removeEventListener('pointerdown', cancelForNavigation, true);
      if (yotpoReadAllCleanup === cleanup) {
        yotpoReadAllCleanup = null;
      }
    };
    const cancelForNavigation = (nextEvent) => {
      if (nextEvent.type === 'pointerdown' || nextEvent.key === 'Tab' || nextEvent.key === 'Escape') {
        cleanup();
      }
    };
    const completeWhenClosed = () => {
      if (completed || (control.isConnected && isElementVisible(dialog))) {
        return;
      }

      // Wait one paint so Yotpo can finish removing the modal and restoring
      // the background before the first programmatic destination focus.
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(() => {
        focusFrame = null;
        if (completed || (control.isConnected && isElementVisible(dialog))) {
          return;
        }

        if (!focusDeadline) {
          focusDeadline = performance.now() + YOTPO_READ_ALL_FOCUS_WINDOW;
        }

        // Yotpo can restore focus to a product control after this callback.
        // Reassert the exact Customer Reviews group for a short, bounded
        // window. Once the group owns focus, focus() is not called again, so
        // this does not repeatedly announce the group to a screen reader.
        focusYotpoReviewsGroup();
        if (performance.now() < focusDeadline) {
          window.clearTimeout(retryTimer);
          retryTimer = window.setTimeout(completeWhenClosed, YOTPO_READ_ALL_FOCUS_RETRY_DELAY);
        } else {
          cleanup();
        }
      });
    };
    const closeObserver = new MutationObserver(completeWhenClosed);
    closeObserver.observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['aria-hidden', 'inert', 'style'],
    });
    document.addEventListener('keydown', cancelForNavigation, true);
    document.addEventListener('pointerdown', cancelForNavigation, true);
    timeout = window.setTimeout(cleanup, 3000);
    yotpoReadAllCleanup = cleanup;
    queueMicrotask(completeWhenClosed);
  }

  function handleYotpoFocusIn(event) {
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const dialog = eventPath.find((element) => (
      element instanceof HTMLElement && element.matches('[role="dialog"], [aria-modal="true"]')
    ));

    if (dialog && getYotpoSummaryDialogs().includes(dialog)) {
      normalizeYotpoSummaryDialog(dialog);
    }
  }

  function handleYotpoKeydown(event) {
    if (event.defaultPrevented || (event.key !== ' ' && event.key !== 'Enter')) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;
    const summaryBlock = target?.closest(YOTPO_SUMMARY_BLOCK_SELECTOR);
    const control = summaryBlock ? getYotpoSummaryControl(summaryBlock) : null;

    if (!control || !control.contains(target) || control.matches('button, a[href], input, select, textarea')) {
      return;
    }

    event.preventDefault();
    control.click();
  }

  function scheduleYotpoAccessibilitySync() {
    YOTPO_SYNC_DELAYS.forEach((delay) => window.setTimeout(queueYotpoAccessibilitySync, delay));

    if (!yotpoObserver && document.body) {
      yotpoObserver = new MutationObserver(queueYotpoAccessibilitySync);
      yotpoObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function scheduleInitialSyncs() {
    if (!hasGoogleOfferSignal()) {
      return;
    }

    SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(queueSync, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialSyncs, { once: true });
  } else {
    scheduleInitialSyncs();
  }

  document.addEventListener('shopify:section:load', scheduleInitialSyncs);
  document.addEventListener('DOMContentLoaded', scheduleProductGalleryWishlistSync, { once: true });
  document.addEventListener('shopify:section:load', scheduleProductGalleryWishlistSync);
  document.addEventListener('product:rerender', scheduleProductGalleryWishlistSync, true);
  document.addEventListener('click', handleYotpoReadAllReviews, true);
  document.addEventListener('click', handleYotpoClick);
  document.addEventListener('focusin', handleYotpoFocusIn, true);
  document.addEventListener('keydown', handleYotpoKeydown);
  document.addEventListener('DOMContentLoaded', scheduleYotpoAccessibilitySync, { once: true });
  document.addEventListener('shopify:section:load', scheduleYotpoAccessibilitySync);
  if (document.readyState !== 'loading') {
    scheduleProductGalleryWishlistSync();
    scheduleYotpoAccessibilitySync();
  }
})();

/*
  CODEX CUSTOMIZATION START
  Scope: product-page multi-column scroll regions
  Notes:
  - Active product-page Multi-column sections are horizontally scrollable on
    narrow product pages when they do not stack on mobile.
  - Only the overflowing region becomes a Tab stop; static cards and their
    contents remain outside the normal keyboard sequence.
  - Touch scrolling and the existing desktop layout remain native.
  CODEX CUSTOMIZATION END
*/

(() => {
  const PRODUCT_SCROLL_SELECTOR = '[data-codex-product-scroll="true"]';
  const PRODUCT_SCROLL_FOCUS_ATTRIBUTE = 'data-codex-product-scroll-focus';
  const observedRegions = new WeakSet();
  let syncQueued = false;

  function hasHorizontalOverflow(region) {
    if (!(region instanceof HTMLElement)) {
      return false;
    }

    const styles = window.getComputedStyle(region);

    return region.scrollWidth > region.clientWidth + 1
      && ['auto', 'scroll'].includes(styles.overflowX);
  }

  function syncProductScrollRegion(region) {
    if (!(region instanceof HTMLElement)) {
      return;
    }

    if (hasHorizontalOverflow(region)) {
      region.setAttribute('tabindex', '0');
      region.setAttribute(PRODUCT_SCROLL_FOCUS_ATTRIBUTE, 'true');
      return;
    }

    region.removeAttribute('tabindex');
    region.removeAttribute(PRODUCT_SCROLL_FOCUS_ATTRIBUTE);
  }

  function observeProductScrollRegion(region) {
    if (!(region instanceof HTMLElement) || observedRegions.has(region)) {
      return;
    }

    observedRegions.add(region);

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(() => syncProductScrollRegion(region));
      resizeObserver.observe(region);
    }
  }

  function syncProductScrollRegions() {
    document.querySelectorAll(PRODUCT_SCROLL_SELECTOR).forEach((region) => {
      syncProductScrollRegion(region);
      observeProductScrollRegion(region);
    });
  }

  function queueProductScrollSync() {
    if (syncQueued) {
      return;
    }

    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncProductScrollRegions();
    });
  }

  function getProductScrollStep(region) {
    const firstItem = region.querySelector(':scope > .multi-column__item');
    const itemWidth = firstItem instanceof HTMLElement
      ? firstItem.getBoundingClientRect().width
      : region.clientWidth * 0.8;
    const styles = window.getComputedStyle(region);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return Math.max(Math.round(itemWidth + gap), 1);
  }

  function handleProductScrollKeydown(event) {
    const region = event.target instanceof HTMLElement
      && event.target.matches(PRODUCT_SCROLL_SELECTOR)
      && event.target.getAttribute(PRODUCT_SCROLL_FOCUS_ATTRIBUTE) === 'true'
      ? event.target
      : null;

    if (!(region instanceof HTMLElement)
      || event.altKey
      || event.ctrlKey
      || event.metaKey
      || event.shiftKey) {
      return;
    }

    const maxScrollLeft = Math.max(region.scrollWidth - region.clientWidth, 0);
    const scrollStep = getProductScrollStep(region);
    let nextScrollLeft = null;

    switch (event.key) {
      case 'ArrowLeft':
        nextScrollLeft = Math.max(region.scrollLeft - scrollStep, 0);
        break;
      case 'ArrowRight':
        nextScrollLeft = Math.min(region.scrollLeft + scrollStep, maxScrollLeft);
        break;
      case 'Home':
        nextScrollLeft = 0;
        break;
      case 'End':
        nextScrollLeft = maxScrollLeft;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    region.scrollTo({ left: nextScrollLeft, behavior: 'auto' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncProductScrollRegions, { once: true });
  } else {
    syncProductScrollRegions();
  }

  document.addEventListener('keydown', handleProductScrollKeydown, true);
  document.addEventListener('shopify:section:load', queueProductScrollSync);
  document.addEventListener('shopify:section:reorder', queueProductScrollSync);
  window.addEventListener('resize', queueProductScrollSync, { passive: true });
  window.addEventListener('orientationchange', queueProductScrollSync);
})();

/*
  CODEX CUSTOMIZATION START
  Scope: Rise.ai gift card product template
  Notes:
  - Rise.ai injects its Send as a Gift button into the theme's buy-buttons element.
  - Product variant rerenders replace that element, so preserve and reattach the injected node on the gift-card template.
  CODEX CUSTOMIZATION END
*/

(() => {
  const GIFT_CARD_TEMPLATE = 'gift-card';
  const BUY_BUTTONS_SELECTOR = `buy-buttons[template="${GIFT_CARD_TEMPLATE}"]`;
  const RISE_BUTTON_SELECTOR = '.GiftWizard-gift-button, .gwbutton';
  const RISE_UPSELL_SELECTOR = '.gbbExtBundleUpsellWrapper';
  const RISE_POPUP_SELECTOR = '.Rise-popup';
  const RISE_POPUP_IFRAME_SELECTOR = 'iframe[src*="str.rise-ai.com/gift"]';
  const RISE_POPUP_CLOSE_SELECTOR = '.Rise-popup-close-button';
  const READY_CLASS = 'codex-rise-gift-ready';
  const SYNC_DELAYS = [0, 100, 300, 750, 1500, 3000, 5000];
  const DIALOG_NAME = 'Send a gift card';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const preservedNodesByForm = new Map();
  const backgroundStateByElement = new Map();
  const observedRisePopups = new WeakSet();
  let syncQueued = false;
  let observer = null;
  let popupObserver = null;
  let activeRiseDialog = null;
  let riseDialogOpener = null;
  let lastDialogFocus = null;
  let focusRestoreFrame = null;

  function getState(formId) {
    if (!preservedNodesByForm.has(formId)) {
      preservedNodesByForm.set(formId, {});
    }

    return preservedNodesByForm.get(formId);
  }

  function rememberInjectedNodes(buyButtons, state) {
    const riseButtons = Array.from(buyButtons.querySelectorAll(RISE_BUTTON_SELECTOR));
    const riseUpsellWrapper = buyButtons.querySelector(RISE_UPSELL_SELECTOR);

    if (riseButtons.length > 0) {
      const preferredButton = state.riseButton instanceof HTMLElement && riseButtons.includes(state.riseButton) ? state.riseButton : riseButtons[0];

      state.riseButton = preferredButton;

      riseButtons.forEach((riseButton) => {
        if (riseButton !== preferredButton) {
          riseButton.remove();
        }
      });
    }

    if (riseUpsellWrapper instanceof HTMLElement) {
      state.riseUpsellWrapper = riseUpsellWrapper;
    }
  }

  function restoreInjectedNodes(buyButtons, state) {
    if (state.riseButton instanceof HTMLElement && !buyButtons.contains(state.riseButton)) {
      buyButtons.append(state.riseButton);
    }

    if (state.riseUpsellWrapper instanceof HTMLElement && !buyButtons.contains(state.riseUpsellWrapper)) {
      buyButtons.append(state.riseUpsellWrapper);
    }
  }

  function isVisible(element) {
    if (!(element instanceof HTMLElement) || element.hidden || element.getClientRects().length === 0) {
      return false;
    }

    const style = window.getComputedStyle(element);

    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function getFocusableElements(dialog) {
    return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      return element instanceof HTMLElement
        && element.tabIndex >= 0
        && element.getAttribute('aria-hidden') !== 'true'
        && !element.closest('[inert]')
        && isVisible(element);
    });
  }

  function focusElement(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) {
      return false;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }

    return document.activeElement === element;
  }

  function getPopupContainer(dialog) {
    const popupContainer = dialog.parentElement;

    return popupContainer instanceof HTMLElement ? popupContainer : dialog;
  }

  function makeBackgroundInert(dialog) {
    const popupContainer = getPopupContainer(dialog);
    const ignoredTags = new Set(['LINK', 'META', 'NOSCRIPT', 'SCRIPT', 'STYLE', 'TEMPLATE']);

    Array.from(document.body.children).forEach((element) => {
      if (!(element instanceof HTMLElement)
        || ignoredTags.has(element.tagName)
        || element === popupContainer
        || element.contains(popupContainer)) {
        return;
      }

      if (!backgroundStateByElement.has(element)) {
        backgroundStateByElement.set(element, {
          ariaHidden: element.getAttribute('aria-hidden'),
          hadInert: element.hasAttribute('inert'),
        });
      }

      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    });
  }

  function restoreBackground() {
    backgroundStateByElement.forEach((state, element) => {
      if (!element.isConnected) {
        return;
      }

      if (state.ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', state.ariaHidden);
      }

      if (state.hadInert) {
        element.setAttribute('inert', '');
      } else {
        element.removeAttribute('inert');
      }
    });

    backgroundStateByElement.clear();
  }

  function enhanceRiseDialog(dialog) {
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', DIALOG_NAME);
    dialog.setAttribute('tabindex', '-1');

    const iframe = dialog.querySelector(RISE_POPUP_IFRAME_SELECTOR);

    if (iframe instanceof HTMLIFrameElement && iframe.title.trim() === '') {
      iframe.title = DIALOG_NAME;
    }

    const closeButton = dialog.querySelector(RISE_POPUP_CLOSE_SELECTOR);

    if (closeButton instanceof HTMLButtonElement) {
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', `Close ${DIALOG_NAME.toLowerCase()}`);
    }
  }

  function focusInitialDialogControl(dialog) {
    const closeButton = dialog.querySelector(RISE_POPUP_CLOSE_SELECTOR);
    const iframe = dialog.querySelector(RISE_POPUP_IFRAME_SELECTOR);
    const focusableElements = getFocusableElements(dialog);
    const target = isVisible(closeButton)
      ? closeButton
      : isVisible(iframe)
        ? iframe
        : focusableElements[0] || dialog;

    focusElement(target);
  }

  function activateRiseDialog(dialog) {
    const focusedElement = document.activeElement;

    if (!(riseDialogOpener instanceof HTMLElement)
      && focusedElement instanceof HTMLElement
      && focusedElement !== document.body
      && !dialog.contains(focusedElement)) {
      riseDialogOpener = focusedElement;
    }

    activeRiseDialog = dialog;
    lastDialogFocus = null;
    enhanceRiseDialog(dialog);
    makeBackgroundInert(dialog);

    window.requestAnimationFrame(() => {
      if (activeRiseDialog === dialog && dialog.classList.contains('open') && !dialog.contains(document.activeElement)) {
        focusInitialDialogControl(dialog);
      }
    });
  }

  function deactivateRiseDialog({ preserveOpener = false, restoreFocus = true } = {}) {
    const opener = riseDialogOpener;

    activeRiseDialog = null;
    riseDialogOpener = preserveOpener ? opener : null;
    lastDialogFocus = null;
    restoreBackground();

    if (!restoreFocus) {
      return;
    }

    if (focusRestoreFrame !== null) {
      window.cancelAnimationFrame(focusRestoreFrame);
    }

    focusRestoreFrame = window.requestAnimationFrame(() => {
      focusRestoreFrame = null;

      if (activeRiseDialog !== null) {
        return;
      }

      const fallbackOpener = document.querySelector(`${BUY_BUTTONS_SELECTOR} ${RISE_BUTTON_SELECTOR}`);
      const focusTarget = opener instanceof HTMLElement && opener.isConnected ? opener : fallbackOpener;

      if (focusTarget instanceof HTMLElement && isVisible(focusTarget)) {
        focusElement(focusTarget);
      }
    });
  }

  function syncRiseDialog() {
    const openDialog = document.querySelector(`${RISE_POPUP_SELECTOR}.open`);

    if (activeRiseDialog instanceof HTMLElement && activeRiseDialog !== openDialog) {
      deactivateRiseDialog({
        preserveOpener: openDialog instanceof HTMLElement,
        restoreFocus: openDialog === null,
      });
    }

    if (openDialog instanceof HTMLElement) {
      if (activeRiseDialog !== openDialog) {
        activateRiseDialog(openDialog);
      } else {
        enhanceRiseDialog(openDialog);
        makeBackgroundInert(openDialog);
      }
    }
  }

  function observeRisePopups() {
    if (!popupObserver) {
      popupObserver = new MutationObserver(queueSync);
    }

    document.querySelectorAll(RISE_POPUP_SELECTOR).forEach((popup) => {
      if (!(popup instanceof HTMLElement) || observedRisePopups.has(popup)) {
        return;
      }

      observedRisePopups.add(popup);
      popupObserver.observe(popup, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }

  function handleRiseTriggerClick(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const trigger = event.target.closest(RISE_BUTTON_SELECTOR);

    if (trigger instanceof HTMLElement && trigger.closest(BUY_BUTTONS_SELECTOR)) {
      riseDialogOpener = trigger;
    }
  }

  function handleDialogKeydown(event) {
    const dialog = activeRiseDialog;

    if (!(dialog instanceof HTMLElement)) {
      return;
    }

    if (event.key === 'Escape') {
      const closeButton = dialog.querySelector(RISE_POPUP_CLOSE_SELECTOR);

      if (closeButton instanceof HTMLButtonElement) {
        event.preventDefault();
        event.stopPropagation();
        closeButton.click();
        window.requestAnimationFrame(queueSync);
      }

      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(dialog);
    const firstFocusable = focusableElements[0] || dialog;
    const lastFocusable = focusableElements[focusableElements.length - 1] || dialog;
    const focusedElement = document.activeElement;

    if (!dialog.contains(focusedElement)) {
      event.preventDefault();
      focusElement(event.shiftKey ? lastFocusable : firstFocusable);
    } else if (event.shiftKey && (focusedElement === firstFocusable || focusedElement === dialog)) {
      event.preventDefault();
      focusElement(lastFocusable);
    } else if (!event.shiftKey && focusedElement === lastFocusable) {
      event.preventDefault();
      focusElement(firstFocusable);
    }
  }

  function handleDialogFocusIn(event) {
    const dialog = activeRiseDialog;

    if (!(dialog instanceof HTMLElement) || !(event.target instanceof HTMLElement)) {
      return;
    }

    if (dialog.contains(event.target)) {
      lastDialogFocus = event.target;
      return;
    }

    window.requestAnimationFrame(() => {
      if (!(activeRiseDialog instanceof HTMLElement) || activeRiseDialog.contains(document.activeElement)) {
        return;
      }

      const focusableElements = getFocusableElements(activeRiseDialog);
      const iframe = activeRiseDialog.querySelector(RISE_POPUP_IFRAME_SELECTOR);
      const focusTarget = lastDialogFocus === iframe
        ? focusableElements[focusableElements.length - 1]
        : focusableElements[0];

      focusElement(focusTarget || activeRiseDialog);
    });
  }

  function handleWindowFocus() {
    if (!(activeRiseDialog instanceof HTMLElement) || activeRiseDialog.contains(document.activeElement)) {
      return;
    }

    const focusableElements = getFocusableElements(activeRiseDialog);
    const iframe = activeRiseDialog.querySelector(RISE_POPUP_IFRAME_SELECTOR);
    const focusTarget = lastDialogFocus === iframe
      ? focusableElements[focusableElements.length - 1]
      : focusableElements[0];

    window.requestAnimationFrame(() => {
      if (activeRiseDialog instanceof HTMLElement && !activeRiseDialog.contains(document.activeElement)) {
        focusElement(focusTarget || activeRiseDialog);
      }
    });
  }

  function handleDialogFocusOut(event) {
    const dialog = activeRiseDialog;

    if (!(dialog instanceof HTMLElement)
      || !(event.target instanceof HTMLElement)
      || !dialog.contains(event.target)) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (!(activeRiseDialog instanceof HTMLElement) || activeRiseDialog.contains(document.activeElement)) {
        return;
      }

      const focusableElements = getFocusableElements(activeRiseDialog);
      const iframe = activeRiseDialog.querySelector(RISE_POPUP_IFRAME_SELECTOR);
      const focusTarget = event.target === iframe
        ? focusableElements[focusableElements.length - 1]
        : focusableElements[0];

      focusElement(focusTarget || activeRiseDialog);
    });
  }

  function syncRiseGiftButtons() {
    document.querySelectorAll(BUY_BUTTONS_SELECTOR).forEach((buyButtons) => {
      const formId = buyButtons.getAttribute('form') || '';

      if (formId === '') {
        return;
      }

      const state = getState(formId);

      rememberInjectedNodes(buyButtons, state);
      restoreInjectedNodes(buyButtons, state);
      rememberInjectedNodes(buyButtons, state);

      buyButtons.classList.toggle(READY_CLASS, state.riseButton instanceof HTMLElement && buyButtons.contains(state.riseButton));
    });

    observeRisePopups();
    syncRiseDialog();
  }

  function queueSync() {
    if (syncQueued) {
      return;
    }

    syncQueued = true;

    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncRiseGiftButtons();
    });
  }

  function scheduleSyncs() {
    SYNC_DELAYS.forEach((delay) => {
      window.setTimeout(queueSync, delay);
    });
  }

  function hasGiftCardBuyButtons() {
    return document.querySelector(BUY_BUTTONS_SELECTOR) !== null;
  }

  function formHasGiftCardBuyButtons(form) {
    if (!(form instanceof HTMLFormElement) || form.id === '') {
      return false;
    }

    return Array.from(document.querySelectorAll(BUY_BUTTONS_SELECTOR)).some((buyButtons) => buyButtons.getAttribute('form') === form.id);
  }

  function startRiseGiftCardSync() {
    if (!hasGiftCardBuyButtons()) {
      return;
    }

    syncRiseGiftButtons();
    scheduleSyncs();

    if (!observer) {
      observer = new MutationObserver(queueSync);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startRiseGiftCardSync, { once: true });
  } else {
    startRiseGiftCardSync();
  }

  document.addEventListener('product:rerender', (event) => {
    if (formHasGiftCardBuyButtons(event.target)) {
      scheduleSyncs();
    }
  }, true);

  document.addEventListener('variant:change', (event) => {
    if (formHasGiftCardBuyButtons(event.target)) {
      scheduleSyncs();
    }
  });

  document.addEventListener('click', handleRiseTriggerClick, true);
  document.addEventListener('keydown', handleDialogKeydown, true);
  document.addEventListener('focusin', handleDialogFocusIn, true);
  document.addEventListener('focusout', handleDialogFocusOut, true);
  window.addEventListener('focus', handleWindowFocus, true);
  document.addEventListener('shopify:section:load', startRiseGiftCardSync);
})();
